import { Eye, EyeOff, Loader2, MapPin, Plus, UserPlus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import LocationSelector, { type LocationDetails } from '../LocationSelector';
import { useRoles } from '../../hooks/useRoles';
import { resolveLegacyRoleForCreate } from '../../lib/createUserRole';
import type { CreateUserDto, CreateUserRequestStatus } from '../../types/api';
import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './Dialog';
import { Input } from './Input';

const EMPTY_LOCATION: LocationDetails = {
  provinceId: null,
  districtId: null,
  sectorId: null,
  cellId: null,
  villageId: null,
};

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: CreateUserDto) => void;
  isLoading?: boolean;
}

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [status, setStatus] = useState<CreateUserRequestStatus>('ACTIVE');
  const [isCentralLevel, setIsCentralLevel] = useState(false);
  const [locationIds, setLocationIds] = useState<{ id: string; name: string }[]>([]);
  const [pickerLocation, setPickerLocation] = useState<LocationDetails>(EMPTY_LOCATION);
  const [deepestLocation, setDeepestLocation] = useState<{ id: string; name: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: rolesResponse, isLoading: rolesLoading } = useRoles({ limit: 500 });
  const prevOpenRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const prev = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open !== true || prev !== false) return;

    setName('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setRoleId('');
    setStatus('ACTIVE');
    setIsCentralLevel(false);
    setLocationIds([]);
    setPickerLocation(EMPTY_LOCATION);
    setDeepestLocation(null);
    setErrors({});
  }, [open]);

  const handleAddLocation = () => {
    if (!deepestLocation || locationIds.some(l => l.id === deepestLocation.id)) return;
    setLocationIds(prev => [...prev, deepestLocation]);
    setPickerLocation(EMPTY_LOCATION);
    setDeepestLocation(null);
  };

  const removeLocation = (id: string) => {
    setLocationIds(prev => prev.filter(l => l.id !== id));
  };

  const handleConfirm = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Invalid email';
    if (!password) errs.password = 'Password is required';
    else if (password.length < 6) errs.password = 'Password must be at least 6 characters';
    if (!roleId) errs.roleId = 'Role is required';

    const selectedRole = rolesResponse?.data?.find(r => r.id === roleId);
    if (!selectedRole) errs.roleId = errs.roleId || 'Invalid role';

    if (!isCentralLevel && locationIds.length === 0) {
      errs.locations = 'Add at least one location, or enable central-level user';
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const data: CreateUserDto = {
      name: name.trim(),
      email: email.trim(),
      password,
      role: resolveLegacyRoleForCreate(selectedRole!.code),
      roleId,
      status,
      isCentralLevel,
      geoLocationIds: locationIds.map(l => l.id),
    };

    onConfirm(data);
  };

  const selectClass =
    'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <Dialog open={open} onOpenChange={v => !isLoading && onOpenChange(v)}>
      <DialogContent showCloseButton={!isLoading} className='max-w-lg max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-start gap-3'>
            <UserPlus className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
            <div>
              <DialogTitle>Create user</DialogTitle>
              <DialogDescription className='mt-1'>
                Create a new account. The backend may require approval depending on status.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4 pt-1'>
          <div>
            <label htmlFor='create-name' className='block text-sm font-medium text-gray-700 mb-1'>
              Name <span className='text-red-500'>*</span>
            </label>
            <Input
              id='create-name'
              value={name}
              onChange={e => {
                setName(e.target.value);
                setErrors(p => ({ ...p, name: '' }));
              }}
              placeholder='Full name'
              className={errors.name ? 'border-red-500' : ''}
              disabled={isLoading}
            />
            {errors.name && <p className='mt-1 text-xs text-red-600'>{errors.name}</p>}
          </div>

          <div>
            <label htmlFor='create-email' className='block text-sm font-medium text-gray-700 mb-1'>
              Email <span className='text-red-500'>*</span>
            </label>
            <Input
              id='create-email'
              type='email'
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setErrors(p => ({ ...p, email: '' }));
              }}
              placeholder='Email address'
              className={errors.email ? 'border-red-500' : ''}
              disabled={isLoading}
            />
            {errors.email && <p className='mt-1 text-xs text-red-600'>{errors.email}</p>}
          </div>

          <div>
            <label htmlFor='create-password' className='block text-sm font-medium text-gray-700 mb-1'>
              Password <span className='text-red-500'>*</span>
            </label>
            <div className='relative'>
              <Input
                id='create-password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setErrors(p => ({ ...p, password: '' }));
                }}
                placeholder='Initial password'
                className={`pr-10 ${errors.password ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
              <button
                type='button'
                onClick={() => setShowPassword(v => !v)}
                className='absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
              </button>
            </div>
            {errors.password && <p className='mt-1 text-xs text-red-600'>{errors.password}</p>}
          </div>

          <div>
            <label htmlFor='create-role' className='block text-sm font-medium text-gray-700 mb-1'>
              Role <span className='text-red-500'>*</span>
            </label>
            <select
              id='create-role'
              value={roleId}
              onChange={e => {
                setRoleId(e.target.value);
                setErrors(p => ({ ...p, roleId: '' }));
              }}
              className={selectClass}
              disabled={isLoading || rolesLoading}
            >
              <option value=''>Select role</option>
              {(rolesResponse?.data ?? []).map(r => (
                <option key={r.id} value={r.id}>
                  {r.name || r.code}
                </option>
              ))}
            </select>
            {errors.roleId && <p className='mt-1 text-xs text-red-600'>{errors.roleId}</p>}
          </div>

          <div>
            <label htmlFor='create-status' className='block text-sm font-medium text-gray-700 mb-1'>
              Status
            </label>
            <select
              id='create-status'
              value={status}
              onChange={e => setStatus(e.target.value as CreateUserRequestStatus)}
              className={selectClass}
              disabled={isLoading}
            >
              <option value='INACTIVE'>Inactive (pending)</option>
              <option value='ACTIVE'>Active</option>
              <option value='REJECTED'>Rejected</option>
            </select>
          </div>

          <label className='flex items-center gap-2 text-sm text-gray-700 cursor-pointer'>
            <input
              type='checkbox'
              checked={isCentralLevel}
              onChange={e => {
                setIsCentralLevel(e.target.checked);
                setErrors(p => ({ ...p, locations: '' }));
              }}
              disabled={isLoading}
              className='rounded border-gray-300'
            />
            Central-level user (not tied to a single geography)
          </label>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              <MapPin className='inline h-3.5 w-3.5 mr-1 mb-0.5' />
              Assigned locations
            </label>
            {errors.locations && <p className='mb-2 text-xs text-red-600'>{errors.locations}</p>}
            {locationIds.length > 0 ? (
              <div className='flex flex-wrap gap-1.5 mb-2'>
                {locationIds.map(loc => (
                  <span
                    key={loc.id}
                    className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs bg-secondary text-primary'
                  >
                    {loc.name}
                    <button
                      type='button'
                      onClick={() => removeLocation(loc.id)}
                      className='hover:text-red-600'
                      disabled={isLoading}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className='text-xs text-gray-400 mb-2 italic'>No locations added</p>
            )}

            <div className='rounded-md border border-dashed border-gray-300 p-3 space-y-2 bg-gray-50'>
              <p className='text-xs text-gray-500 font-medium'>Add a location</p>
              <LocationSelector
                locationDetails={pickerLocation}
                setLocationDetails={setPickerLocation}
                onDeepestChange={setDeepestLocation}
                layout='column'
                hideLabels={false}
              />
              <Button
                type='button'
                size='sm'
                variant='outline'
                onClick={handleAddLocation}
                disabled={!deepestLocation || locationIds.some(l => l.id === deepestLocation?.id) || isLoading}
                className='w-full'
              >
                <Plus className='h-3.5 w-3.5 mr-1' />
                Add{deepestLocation ? ` "${deepestLocation.name}"` : ' location'}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button type='button' onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin mr-2 inline' />
                Creating…
              </>
            ) : (
              'Create user'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserDialog;
